/** MFE mount factory for simple microfrontend entry points, handling React root lifecycle and window registration.
 *
 *  THE PROVIDER STACK MIRRORS `layout/StandaloneSpaProviders.tsx`, deliberately: an MFE surface is the SAME product
 *  rendered inside a host shell rather than at its own origin, so a view that can raise a success toast standalone must
 *  be able to raise one embedded. Until 2026-08-20 this factory supplied a QueryClient and NOTHING else, so every
 *  embedded view's `useToast()` threw its provider-missing error and each surface answered with a hand-rolled inline
 *  banner instead. The two stacks differ only where they must: no `AuthProvider` here, because the host shell owns the
 *  session and the bundle reads it off the shell global. */

import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { DEFAULT_QUERY_OPTIONS } from '@federa/const/query-conditions';
import { buildMfeReadyEventName } from '@federa/const/mfe-names';
import { MFE_MOUNT_COPY } from '@federa/const/mfe-mount';
import { buildMfeGlobalKey } from './mfe-global-key.ts';
import { ToastProvider } from '../feedback/Toast.tsx';
import { RootErrorBoundary } from '../error/RootErrorBoundary.tsx';
import type { ReactNode } from 'react';
import type { Root } from 'react-dom/client';
import type { FederaMFE } from '@federa/types/mfe-bridge';
import type { MfeSlug } from '@federa/types/ui-mfe-slug';

interface MFEMountConfig {
	/** MFE identifier used for window.__federaMFE_{name} and mfe-{name}-ready event. A `MFE_NAMES` member: the host shell dials `__federaMFE_<slug>` off the same SSOT, so a slug the fleet does not carry publishes a global nothing ever reads. */
	name: MfeSlug;
	/** Returns the JSX to render inside QueryClientProvider. Receives the mount container for reading data attributes. */
	renderApp: (container: HTMLElement) => ReactNode;
}

/**
 * Creates and registers a simple MFE with QueryClient and root management: assigns { mount, unmount } to
 * window.__federaMFE_{name} and fires a "mfe-{name}-ready" CustomEvent.
 *
 * IT COMPOSES THE `ToastProvider` FOR EVERY MOUNT (2026-08-20), so NO entry may nest one of its own. A second provider
 * inside the first mints its own toast list and its own timer map, so `useToast()` resolves against whichever is
 * NEARER and a toast raised above it lands in a container nothing on the page renders. Stated here, once, rather than
 * byte-identically at each entry.
 */
export function createMFEMount(config: MFEMountConfig): FederaMFE {
	const { name, renderApp } = config;

	// One React root PER container: an MFE can be mounted into several containers deliberately, so per-container roots keep mounting idempotent and unmounting scoped.
	const roots = new Map<HTMLElement, { root: Root }>();
	// ONE QueryClient per MFE bundle, shared by every container it mounts, alive for the document. Per-container
	// clients meant two roots of the same bundle asking the same question fetched it twice -- the notifications bell
	// and the notifications page each read the list on every arrival at /notifications (measured 2026-09-03) -- and a
	// remount started from an empty cache. Same React instance, same bundle, so one client is the natural scope.
	let queryClient: QueryClient | null = null;

	function mount(container: HTMLElement): void {
		if (roots.has(container)) return;
		queryClient ??= new QueryClient({
			defaultOptions: { queries: DEFAULT_QUERY_OPTIONS },
		});
		const root = createRoot(container);
		// The boundary is OUTERMOST so a throw from the providers themselves is caught too, and it names the slug: an MFE
		// crash otherwise unwinds this root and leaves the host page showing an empty container with the report only in
		// the console -- the host's own boundary cannot see it, because a separately-rooted tree is not in its subtree.
		root.render(
			<RootErrorBoundary boundaryName={`createMFEMount:${name}`} title={MFE_MOUNT_COPY.crashedTitle} body={MFE_MOUNT_COPY.crashedBody}>
				<QueryClientProvider client={queryClient}>
					<ToastProvider>{renderApp(container)}</ToastProvider>
				</QueryClientProvider>
			</RootErrorBoundary>,
		);
		roots.set(container, { root });
	}

	function unmount(container?: HTMLElement): void {
		if (container) {
			const entry = roots.get(container);
			if (entry) {
				entry.root.unmount();
				roots.delete(container);
			}
			return;
		}
		// No container -> tear down every root (backward-compatible with the old no-arg form).
		for (const [, entry] of roots) entry.root.unmount();
		roots.clear();
	}

	const mfe: FederaMFE = { mount, unmount };

	// `Reflect.set` reaches the runtime-composed key without widening `Window`: `globalKey()` is a runtime call, and an
	// ambient index signature must be a template-literal TYPE, so it cannot carry a brand prefix resolved at call time.
	Reflect.set(window, buildMfeGlobalKey(name), mfe);
	void window.dispatchEvent(new CustomEvent(buildMfeReadyEventName(name)));

	return mfe;
}
